import { useEffect, useState } from "react";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";

interface PayoutCalculatorProps {
  cpm: number;
  maximumPaymentCap: number;
  cpmRateType: "totalViews" | "perVideo";
  flatFeePerVideo?: number;
  flatFeeTimePeriod?: {
    amount: number;
    days: number;
  };
  contractDurationDays: number;
  requiredVideos?: {
    count: number;
    duration: number;
  };
}

export function PayoutCalculator({
  cpm,
  maximumPaymentCap,
  cpmRateType,
  flatFeePerVideo = 0,
  flatFeeTimePeriod,
  contractDurationDays,
  requiredVideos,
}: PayoutCalculatorProps) {
  const [totalViews, setTotalViews] = useState([1000000]); // Start with 1M views
  const [inputValue, setInputValue] = useState("1,000,000");

  // Calculate default video count based on contract duration and required videos
  const defaultVideoCount =
    requiredVideos && requiredVideos.count > 0
      ? requiredVideos.count *
        Math.ceil(contractDurationDays / requiredVideos.duration)
      : 1;

  const [videoCount, setVideoCount] = useState(Math.max(defaultVideoCount, 1));

  // Update video count when required videos or contract duration changes
  useEffect(() => {
    if (requiredVideos && requiredVideos.count > 0) {
      const newVideoCount =
        requiredVideos.count *
        Math.ceil(contractDurationDays / requiredVideos.duration);
      setVideoCount(Math.max(newVideoCount, 1));
    } else {
      setVideoCount(1);
    }
  }, [requiredVideos, contractDurationDays]);

  // Format number with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString("en-US");
  };

  // Format currency
  const formatCurrency = (amount: number) =>
    amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });

  // Calculate recurring payments if applicable
  const calculateRecurringPayments = () => {
    if (!flatFeeTimePeriod) return 0;
    const numberOfPeriods = Math.ceil(
      contractDurationDays / flatFeeTimePeriod.days,
    );
    return flatFeeTimePeriod.amount * numberOfPeriods;
  };

  // Calculate earnings based on user-specified video count
  const calculateEarnings = (views: number, numVideos: number) => {
    // Calculate flat fees
    const flatFeeEarnings = numVideos * (flatFeePerVideo || 0);
    const recurringPayments = calculateRecurringPayments();

    // Treat 0 or falsy maximumPaymentCap as unlimited
    const hasPaymentCap = maximumPaymentCap && maximumPaymentCap > 0;

    if (cpmRateType === "totalViews") {
      // For total views, the payment is capped across all videos (if there's a cap)
      const viewBasedEarnings = hasPaymentCap
        ? Math.min((views * cpm) / 1000, maximumPaymentCap)
        : (views * cpm) / 1000;
      const viewsPerVideo = Math.ceil(views / numVideos);

      return {
        viewsPerVideo,
        earnings: {
          fromViews: viewBasedEarnings,
          fromFlatFees: flatFeeEarnings,
          fromRecurring: recurringPayments,
          total: viewBasedEarnings + flatFeeEarnings + recurringPayments,
        },
        maxViewsBeforeCap: hasPaymentCap
          ? (maximumPaymentCap * 1000) / cpm
          : Infinity,
        isCapped: hasPaymentCap && (views * cpm) / 1000 > maximumPaymentCap,
        hasPaymentCap,
      };
    } else {
      // For per-video cap, calculate earnings with user-specified video count
      const viewsPerVideo = Math.ceil(views / numVideos);
      const viewEarningsPerVideo = hasPaymentCap
        ? Math.min((viewsPerVideo * cpm) / 1000, maximumPaymentCap)
        : (viewsPerVideo * cpm) / 1000;
      const totalViewEarnings = viewEarningsPerVideo * numVideos;
      const maxViewsPerVideo = hasPaymentCap
        ? (maximumPaymentCap * 1000) / cpm
        : Infinity;
      const minimumVideosNeeded = hasPaymentCap
        ? Math.ceil(views / maxViewsPerVideo)
        : 1;

      return {
        viewsPerVideo,
        earnings: {
          fromViews: totalViewEarnings,
          fromFlatFees: flatFeeEarnings,
          fromRecurring: recurringPayments,
          total: totalViewEarnings + flatFeeEarnings + recurringPayments,
        },
        maxViewsBeforeCap: maxViewsPerVideo,
        isCapped: hasPaymentCap && viewsPerVideo > maxViewsPerVideo,
        isOptimal: !hasPaymentCap || numVideos >= minimumVideosNeeded,
        hasPaymentCap,
      };
    }
  };

  const earnings = calculateEarnings(totalViews[0], videoCount);

  // Handle text input changes for views
  const handleViewsInputChange = (value: string) => {
    setInputValue(value);
    const numericValue = parseInt(value.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(numericValue)) {
      const boundedValue = Math.min(Math.max(numericValue, 100000), 10000000);
      setTotalViews([boundedValue]);
    }
  };

  // Handle input blur for views
  const handleViewsInputBlur = () => {
    setInputValue(formatNumber(totalViews[0]));
  };

  // Handle video count changes
  const handleVideoCountChange = (value: string) => {
    const numericValue = parseInt(value, 10);
    if (!isNaN(numericValue) && numericValue > 0) {
      setVideoCount(numericValue);
    } else if (value === "" || numericValue === 0) {
      // If empty or 0, set to 1 to prevent division by zero
      setVideoCount(1);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium text-primary mb-4">
        Earnings Calculator
      </h3>
      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center text-sm text-primary/60 mb-2">
              <span>Total Views Target</span>
              <div className="w-40">
                <Input
                  type="text"
                  value={inputValue}
                  onChange={(e) => handleViewsInputChange(e.target.value)}
                  onBlur={handleViewsInputBlur}
                  className="text-right font-mono text-base"
                  placeholder="Enter views..."
                />
              </div>
            </div>
            <Slider
              value={totalViews}
              onValueChange={(value) => {
                setTotalViews(value);
                setInputValue(formatNumber(value[0]));
              }}
              min={100000}
              max={10000000}
              step={100000}
              className="w-full"
            />
          </div>

          <div>
            <div className="flex justify-between items-center text-sm text-primary/60 mb-2">
              <span>Number of Videos</span>
              <div className="w-40">
                <Input
                  type="number"
                  min="1"
                  value={videoCount}
                  onChange={(e) => handleVideoCountChange(e.target.value)}
                  className="text-right font-mono text-base"
                  placeholder="Enter number of videos..."
                />
              </div>
            </div>
            {requiredVideos && requiredVideos.count > 0 && (
              <p className="text-xs text-primary/60 mt-1">
                Minimum required: {defaultVideoCount} videos (
                {requiredVideos.count} videos every {requiredVideos.duration}{" "}
                days for {contractDurationDays.toFixed(0)} days)
              </p>
            )}
            {requiredVideos && requiredVideos.count > 0 && (
              <p className="text-xs text-primary/60 italic mt-1">
                Note: This is the total number of videos you need to publish
                across all platforms.
              </p>
            )}
          </div>

          <div className="space-y-4 mt-6 border-t pt-4">
            <div className="space-y-2">
              <div className="text-sm font-medium mb-2">
                Total Earnings Breakdown:
              </div>

              <div className="space-y-2">
                {earnings.earnings.fromViews > 0 && (
                  <div className="flex justify-between items-center text-sm pl-4">
                    <div>
                      <span>Views-based earnings</span>
                      <span className="text-xs text-primary/40 block">
                        {formatNumber(earnings.viewsPerVideo)} views ×{" "}
                        {videoCount} videos at ${cpm} CPM
                        {earnings.isCapped ? " (capped)" : ""}
                      </span>
                    </div>
                    <span className="font-mono">
                      {formatCurrency(earnings.earnings.fromViews)}
                    </span>
                  </div>
                )}

                {flatFeePerVideo > 0 && (
                  <div className="flex justify-between items-center text-sm pl-4">
                    <div>
                      <span>Flat fee earnings</span>
                      <span className="text-xs text-primary/40 block">
                        {formatCurrency(flatFeePerVideo)} × {videoCount} videos
                      </span>
                    </div>
                    <span className="font-mono">
                      {formatCurrency(earnings.earnings.fromFlatFees)}
                    </span>
                  </div>
                )}

                {flatFeeTimePeriod && earnings.earnings.fromRecurring > 0 && (
                  <div className="flex justify-between items-center text-sm pl-4">
                    <div>
                      <span>Recurring payments</span>
                      <span className="text-xs text-primary/40 block">
                        {formatCurrency(flatFeeTimePeriod.amount)} every{" "}
                        {flatFeeTimePeriod.days} days for {contractDurationDays}{" "}
                        days
                      </span>
                    </div>
                    <span className="font-mono">
                      {formatCurrency(earnings.earnings.fromRecurring)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center text-base font-medium pt-2 mt-2 border-t">
                  <span>Total Earnings</span>
                  <span className="font-mono">
                    {formatCurrency(earnings.earnings.total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-xs text-primary/40 mt-4 space-y-1">
              {cpmRateType === "totalViews" ? (
                <p>
                  {earnings.hasPaymentCap
                    ? `Maximum views-based earnings capped at ${formatCurrency(maximumPaymentCap)} across all videos`
                    : "No cap on views-based earnings across all videos"}
                </p>
              ) : (
                <>
                  <p>
                    {earnings.hasPaymentCap
                      ? `Maximum views-based earnings capped at ${formatCurrency(maximumPaymentCap)} per video`
                      : "No cap on views-based earnings per video"}
                  </p>
                  {earnings.hasPaymentCap && earnings.isCapped ? (
                    <p className="text-red-500">
                      Warning: With {videoCount} videos, you'll exceed the
                      per-video earnings cap. Consider using at least{" "}
                      {Math.ceil(totalViews[0] / earnings.maxViewsBeforeCap)}{" "}
                      videos to maximize earnings.
                    </p>
                  ) : null}
                  {earnings.hasPaymentCap &&
                  !earnings.isCapped &&
                  earnings.isOptimal ? (
                    <p className="text-green-500">
                      ✓ This number of videos will allow you to maximize
                      earnings without hitting the cap
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
